import React from 'react'
import PropTypes from 'prop-types'
import { Form, Col } from 'react-bootstrap'
import { observer } from 'mobx-react'
import {
  i18nTranslate,
  isExpressCheckout,
  validateMaxLengthForAddressLine,
  getMaxLength,
  isFeatureEnabled,
  isStateValidationSkippedForCountry,
} from 'src/utils'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import {
  addressContainer,
  checkoutContainer,
  nuskinSubscriptionContainer,
} from 'src/models'
import AddressList2 from '../AddressBook/AddressList2'
import { isToHaveOptionalPhoneSchema } from 'src/views/addresses/addressUtils'
import {
  isToShowLegalAddressOptionInAddressForm,
  hasUserAccountAddress,
} from 'src/deps'
import { IoIosCloseCircle } from 'react-icons/io'
import './styles.scss'
import { APPConfig, convertToBoolean } from 'config/appConfig'
import { normalizeForSearch } from 'src/utils/normalizeForSearch'
import SearchBar from 'src/views/components/Common/SearchBar'
import {
  isPostalCodeBlank,
  isPostalCodeOptionalByShippingAddressForm,
} from 'src/views/components/CommonFormForCS/shippingAddressFormValidation'
import { shouldUseShippingAddressForm } from 'src/views/components/CommonFormForCS/shippingAddressFormConfig'
import {
  initializeLegalAddressPreference as syncLegalAddressPreferenceFromStorage,
  handleLegalAddressRadioOption as persistLegalAddressPreference,
  isLegalAddressYesSelected,
  isLegalAddressNoSelected,
} from './legalAddressPreference'

@observer
class ShippingAddressComponent extends React.Component {
  render() {
    const {
      firstName,
      lastName,
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      email,
      phone,
      country,
      isDefault,
    } = this.props.addressInfo
    return <Col></Col>
  }
}

@observer
class ShippingAddressList extends React.Component {
  // Helper to determine if an address is valid and get error message
  enableCheckoutAddressSearch =
    convertToBoolean(APPConfig?.getAppConfig()?.enableCheckoutAddressSearch) ||
    false

  getAddressValidation = accountAddress => {
    let isValidAddress = true
    let errorMessage = ''
    const { isValidCommunication, communicationErrorMessage } =
      this.validateCommunicationPreferences(accountAddress)

    if (validateMaxLengthForAddressLine(accountAddress)) {
      // Say why: a saved address over the market's line limit (CX115-7746)
      // used to show the generic "Invalid Address", so the shopper could not
      // tell what to fix.
      isValidAddress = false
      errorMessage = i18nTranslate(
        'address.addressLineMaxLengthInfo',
        'Maximum {{maxLength}} characters per address line.'
      ).replace('{{maxLength}}', getMaxLength('addressLine1', 255))
    } else if (!this.isValidAddressFields(accountAddress)) {
      isValidAddress = false
      errorMessage = i18nTranslate(
        'addressBook.invalidAddress',
        'Invalid Address,\nPlease add a new address or update the existing address'
      )
    } else if (!isValidCommunication) {
      isValidAddress = false
      errorMessage = communicationErrorMessage
    }
    return { isValidAddress, errorMessage }
  }

  // Helper to determine if an address should be selected
  isAddressSelected = (accountAddress, currentlySelectedAddress) => {
    if (nuskinSubscriptionContainer?.isToShowBlackOut) {
      return (
        accountAddress?.addressId ==
        nuskinSubscriptionContainer?.subscriptionPreferenceResponse?.addresses
          ?.id
      )
    }
    if (accountAddress?.addressId) {
      if (currentlySelectedAddress?.profileAddressId) {
        return (
          accountAddress?.addressId ==
          currentlySelectedAddress?.profileAddressId
        )
      } else if (currentlySelectedAddress?.addressId) {
        return accountAddress?.addressId == currentlySelectedAddress?.addressId
      } else if (accountAddress?.isDefault) {
        return !!accountAddress?.isDefault
      }
    }
    return false
  }
  constructor(props) {
    super(props)
    this.state = {
      isEdit: null,
      viewMore: false,
      current: null,
      oldlist: addressContainer.addressData,
      newlist: null,
      search: '',
    }
    this.handleViewMore = this.handleViewMore.bind(this)
    this.searchInputRef = React.createRef()
    this.initializeLegalAddressPreference()
  }

  initializeLegalAddressPreference = () => {
    syncLegalAddressPreferenceFromStorage()
  }

  handleSearchChange = e => {
    this.setState({ search: e.target.value })
  }

  handleClearSearch = () => {
    this.setState({ search: '' }, () => {
      this.searchInputRef?.current?.focus?.()
    })
  }

  handleCallback = childData => {
    const editAddressId = this.state.isEdit == childData ? null : childData
    this.setState({ isEdit: editAddressId })
  }

  componentDidMount() {
    const { handleSelect } = this.props
    const { currentlySelectedAddress, addressList } = this.props

    const defaultAddress = addressList.find(address => address.isDefault)
    let isToShowLegalAddressChangesInCheckout =
      isToShowLegalAddressOptionInAddressForm() || false
    if (isToShowLegalAddressChangesInCheckout) {
      this.initializeLegalAddressPreference()
    }
    if (!isExpressCheckout()) {
      if (this.props.isAddressChecked) {
        handleSelect(currentlySelectedAddress)
      } else if (defaultAddress) {
        handleSelect(defaultAddress)
      } else {
        handleSelect(addressList[0])
      }
    }
  }

  handleViewMore() {
    const { viewMore } = this.state
    this.setState({ viewMore: !viewMore })
  }

  renderShippingAddressComponent = (address, isValidAddress) => {
    return (
      <AddressList2
        handleContinueBtnState={this.props.handleContinueBtnState}
        addressInfo={address}
        isEdit={this.state.isEdit}
        handleSelect={this.props.handleSelect}
        radioBtnDisabled={this.props.disableRadioBtn}
        parentCallback={this.handleCallback}
        isValidAddress={isValidAddress}
        validationCallback={this.props.validationCallback}
        isAddressNotAdded={this.props?.isAddressNotAdded}
        setIsAddressNotAdded={this.props?.setIsAddressNotAdded}
        updateAddressAfterCartSuccess={cb =>
          this.props?.updateAddressAfterCartSuccess(cb)
        }
        renderLegalAddressSameAsShippingOptions={
          this.renderLegalAddressSameAsShippingOptions
        }
      />
    )
  }

  isValidAddressFields = accountAddress => {
    const locale = getLocaleCodeFromUrl() || ''
    const isValidState =
      shouldUseShippingAddressForm({
        country: accountAddress?.country,
        type: 'checkout',
      }) || isStateValidationSkippedForCountry(locale)
        ? true
        : accountAddress?.state || ''
    const isZipCodeOptional = isPostalCodeBlank(accountAddress?.zip)
      ? locale === 'es_CO' ||
        locale === 'es_PE' ||
        locale === 'es_CL' ||
        isPostalCodeOptionalByShippingAddressForm(accountAddress)
      : false
    const showAddressLineMaxLengthInfo =
      validateMaxLengthForAddressLine(accountAddress)
    if (showAddressLineMaxLengthInfo) {
      return false
    }

    const isLastNameOptional = isFeatureEnabled('isLastNameOptional')
    const isLastNameValid = isLastNameOptional || accountAddress.lastName

    if (
      accountAddress &&
      accountAddress.addressLine1 &&
      accountAddress.firstName &&
      isLastNameValid &&
      accountAddress.city &&
      isValidState &&
      (isZipCodeOptional || accountAddress.zip)
    ) {
      return true
    } else {
      return false
    }
  }

  validateCommunicationPreferences = accountAddress => {
    let isValidCommunication = true
    let communicationErrorMessage = ''
    let isOptionalPhone = isToHaveOptionalPhoneSchema('checkout') || false
    if (!accountAddress?.phone && !isOptionalPhone) {
      communicationErrorMessage = i18nTranslate(
        'addressBook.invalidPhone',
        'Please enter contact number'
      )
      isValidCommunication = false
    } else if (!checkoutContainer.getShippingAddressEmail(accountAddress)) {
      // a saved address without an email is still usable for a signed-in
      // shopper: Continue fills the profile email in (MDIGI-16357)
      communicationErrorMessage = i18nTranslate(
        'validation.emailErrorText',
        'Please enter email'
      )
      isValidCommunication = false
    }
    return { isValidCommunication, communicationErrorMessage }
  }

  handleLegalAddressRadioOption = (selectedValue = 'false') => {
    persistLegalAddressPreference(selectedValue)
  }

  renderLegalAddressSameAsShippingOptions = () => {
    let isToShowLegalAddressChangesInCheckout =
      isToShowLegalAddressOptionInAddressForm() || false

    if (
      isToShowLegalAddressChangesInCheckout &&
      !hasUserAccountAddress() &&
      !this.props.hideLegalAddressOptions
    ) {
      const radioName = this.props.legalAddressRadioName || 'legalAddressSaved'
      const yesId = `${radioName}Yes`
      const noId = `${radioName}No`
      return (
        <fieldset
          className="legal-address-options-wrapper"
          data-testid="qa-legal-address-same-as-shipping-saved-address">
          <legend className="legal-address-text-wrapper" tabIndex={0}>
            <span className="legal-same-as-shipping d-block mb-2">
              {i18nTranslate(
                'legalAddress.legalAddressSameAsShippingAddress',
                'Is your legal address the same as your shipping address?'
              )}
            </span>
            <span className="legal-address-confirmation-text d-block mb-2">
              {i18nTranslate(
                'legalAddress.addressConfirmationText',
                'This is your permanent address that is used for account and tax purposes.'
              )}
            </span>
          </legend>
          <div className="legal-address-radio-options d-flex">
            <input
              type="radio"
              name={radioName}
              id={yesId}
              data-testid="qa-address-legal-address-same-saved-address"
              onChange={() => {
                this.handleLegalAddressRadioOption('true')
              }}
              checked={isLegalAddressYesSelected()}
            />
            <label htmlFor={yesId} className="col-2 col-md-2">
              {i18nTranslate('legalAddress.yesOption', 'Yes')}
            </label>
            <input
              type="radio"
              name={radioName}
              id={noId}
              data-testid="qa-address-legal-address-not-same-saved-address"
              onChange={() => {
                this.handleLegalAddressRadioOption('false')
              }}
              checked={isLegalAddressNoSelected()}
            />
            <label htmlFor={noId} className="col-2 col-md-2">
              {i18nTranslate('legalAddress.noOption', 'No')}
            </label>
          </div>
        </fieldset>
      )
    }
    return <></>
  }

  render() {
    const {
      currentlySelectedAddress,
      handleSelect,
      isFromItemLevelShipping = false,
      cartItemId,
      addressList,
    } = this.props
    const { viewMore, search } = this.state
    const defaultAddress = addressList.find(address => address.isDefault)
    if (defaultAddress) {
      addressList.splice(
        addressList.findIndex(address => address.isDefault),
        1
      )
      addressList.splice(0, 0, defaultAddress)
    } else {
      const firstAddress = addressList[0]
      addressList[0].isDefault = true
    }

    let filteredList = addressList
    if (search) {
      // removed local `normalize` helper; use shared normalizeForSearch
      const searchText = normalizeForSearch(search).trim()
      if (searchText) {
        const terms = searchText.split(/\s+/).filter(Boolean)
        filteredList = addressList.filter(addr => {
          const zipStr = normalizeForSearch(addr.zip)
          const haystack = [
            addr.firstName,
            addr.lastName,
            addr.addressLine1,
            addr.addressLine2,
            addr.city,
            addr.state,
            addr.stateName,
            addr.stateCode,
            addr.country,
            addr.countryName,
            addr.countryCode,
            zipStr,
          ]
            .map(normalizeForSearch)
            .filter(Boolean)
            .join(' ')
          return terms.every(t => haystack.includes(t))
        })
      }
    }

    const enableScroll = filteredList.length > 10

    const hasQuery = Boolean((search || '').trim())
    const isNoResults = hasQuery && filteredList.length === 0

    return (
      <React.Fragment>
        {this.enableCheckoutAddressSearch && (
          <SearchBar
            ref={this.searchInputRef}
            value={this.state.search}
            onChange={this.handleSearchChange}
            onClear={this.handleClearSearch}
            placeholder={i18nTranslate(
              'address.search.placeholder',
              'Search Address'
            )}
          />
        )}
        <div className={enableScroll ? 'address-list-scroll' : ''}>
          {isNoResults ? (
            <div
              className="address-no-results text-muted py-2"
              role="status"
              aria-live="polite">
              {i18nTranslate('viewCart.searchNoResults', 'No Results Found')}
            </div>
          ) : (
            <>
              {filteredList
                ?.filter(accountAddress =>
                  this.state?.isEdit
                    ? accountAddress?.addressId === this.state?.isEdit
                    : true
                )
                ?.filter((_, index) =>
                  !this.state?.viewMore ? index <= 1 : true
                )
                ?.map(accountAddress => {
                  const { isValidAddress, errorMessage } =
                    this.getAddressValidation(accountAddress)
                  const shouldSelectAddress =
                    this.isAddressSelected(
                      accountAddress,
                      currentlySelectedAddress
                    ) && !checkoutContainer.enableShowAddressList
                  return (
                    <div
                      key={accountAddress.addressId}
                      tabIndex={
                        accountAddress.addressId === this.state.isEdit ? -1 : 0
                      }
                      onKeyDown={e => {
                        if (e.key === 'Enter' && isValidAddress)
                          handleSelect(accountAddress)
                      }}
                      className={`express-address-item bb123 ${
                        nuskinSubscriptionContainer?.isToShowBlackOut
                          ? 'black-out-view-disabled'
                          : ''
                      } ${
                        shouldSelectAddress && isValidAddress
                          ? 'express-address-selected'
                          : ''
                      }`}>
                      {accountAddress.addressId === this.state.isEdit ? (
                        this.renderShippingAddressComponent(
                          accountAddress,
                          isValidAddress
                        )
                      ) : (
                        <>
                          <Form.Check
                            className="my-2 w-100"
                            id={`address-${accountAddress?.addressId}-${cartItemId}`}
                            key={accountAddress?.addressId}
                            label={this.renderShippingAddressComponent(
                              accountAddress
                            )}
                            type="radio"
                            checked={shouldSelectAddress && isValidAddress}
                            disabled={
                              nuskinSubscriptionContainer?.isToShowBlackOut
                                ? true
                                : this.props.disableRadioBtn || !isValidAddress
                            }
                            data-testid={
                              shouldSelectAddress
                                ? 'qa-facet-selected'
                                : 'qa-facet'
                            }
                            onClick={() => {
                              if (isValidAddress) {
                                handleSelect(accountAddress)
                              }
                            }}
                            onChange={() => {}}
                            tabIndex={0}
                          />
                          {!isValidAddress && (
                            <span className="text-danger">{errorMessage}</span>
                          )}
                        </>
                      )}
                    </div>
                  )
                })}
            </>
          )}
        </div>
        {filteredList &&
        filteredList.length > 2 &&
        !viewMore &&
        !this.state.isEdit ? (
          <button className="more-address-button" onClick={this.handleViewMore}>
            {i18nTranslate('accountProfile.viewMoreButton', 'View more')}
          </button>
        ) : (
          <button
            className="more-address-button v-hide-me"
            onClick={this.handleViewMore}>
            {i18nTranslate('accountProfile.viewMoreButton', 'View more')}
          </button>
        )}
        {/* The radio only renders inside the edit form (via AddressList2), never in the plain list */}
      </React.Fragment>
    )
  }
}

export { ShippingAddressComponent, ShippingAddressList }

ShippingAddressList.propTypes = {
  hideLegalAddressOptions: PropTypes.bool,
  legalAddressRadioName: PropTypes.string,
}
